import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import {
  type BrowserSessionSnapshot,
  type DevTunnelRecord,
  type EnvironmentApi,
  type ScopedThreadRef,
  type TerminalSessionSnapshot,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { projectScriptRuntimeEnv } from "@cmmd-forge/shared/projectScripts";
import { Loader2Icon, PlayIcon, TerminalSquareIcon, XIcon } from "lucide-react";
import { type MutableRefObject, useCallback } from "react";
import {
  selectTerminalEventEntries,
  selectTerminalSessionSnapshot,
  selectThreadTerminalSessionSnapshots,
  type TerminalEventEntry,
  type TerminalPreviewUrlRecord,
  useTerminalStateStore,
} from "~/terminalStateStore";
import { toastManager } from "./ui/toast";
import {
  BROWSER_PREVIEW_TERMINAL_ID,
  type BrowserAddressCommand,
  buildPortAwareDevServerCommand,
  latestObservedDevServerPreviewUrl,
  latestTerminalDevServerPreviewUrl,
  localDevServerPreviewPort,
  resolveBrowserDevServerLaunchCardVm,
  resolveBrowserDevServerPreviewAction,
  resolveDevServerDisplayPreviewUrl,
  resolveDevServerLaunchPlan,
  resolveRunningDevServerPreviewLaunch,
  shouldReleaseDevRuntimeBeforeLaunch,
  shouldRunDevServerLaunch,
} from "./BrowserPreviewPanel.logic";

const SCRIPT_TERMINAL_COLS = 120;
const SCRIPT_TERMINAL_ROWS = 30;
const DEV_SERVER_READY_TIMEOUT_MS = 15_000;
const DEV_SERVER_READY_POLL_MS = 250;

interface BrowserPreviewTerminalStateInput {
  readonly terminalIds: ReadonlyArray<string>;
}

interface BrowserPreviewMessageInput {
  readonly text: string;
  readonly createdAt: string;
  readonly completedAt?: string | null | undefined;
}

interface LatestThreadDevServerPreviewUrlInput {
  readonly terminalEventEntriesByKey: Record<string, ReadonlyArray<TerminalEventEntry>>;
  readonly terminalPreviewUrlRecord: TerminalPreviewUrlRecord | null | undefined;
  readonly terminalSessionSnapshotByKey: Record<string, TerminalSessionSnapshot>;
  readonly terminalState: BrowserPreviewTerminalStateInput;
  readonly threadMessages: ReadonlyArray<BrowserPreviewMessageInput>;
  readonly threadRef: ScopedThreadRef;
}

type TerminalLaunchContext = Parameters<
  ReturnType<typeof useTerminalStateStore.getState>["setTerminalLaunchContext"]
>[1];

interface BrowserDevServerLaunchCardProps {
  readonly busy: boolean;
  readonly cardVm: ReturnType<typeof resolveBrowserDevServerLaunchCardVm>;
  readonly launchPlan: ReturnType<typeof resolveDevServerLaunchPlan>;
  readonly onDismiss: () => void;
  readonly onLaunch: () => void;
  readonly onShowTerminals: () => void;
}

export function BrowserDevServerLaunchCard(props: BrowserDevServerLaunchCardProps) {
  return (
    <div className="px-2 py-2">
      <div className="rounded-md border border-border bg-background/70 px-2 py-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
              <PlayIcon className="size-3.5 text-primary" />
              <span>{props.cardVm.title}</span>
            </div>
            {props.launchPlan.source === "heuristic" ? (
              <p className="mt-0.5 text-[11px] text-muted-foreground">{props.cardVm.description}</p>
            ) : (
              <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                {props.launchPlan.name}:{" "}
                <span className="font-mono">{props.launchPlan.command}</span>
              </p>
            )}
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {props.cardVm.terminalSummary}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Dismiss app suggestion"
            onClick={props.onDismiss}
          >
            <XIcon className="size-3.5" />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <button
            type="button"
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            disabled={props.busy}
            onClick={props.onLaunch}
          >
            {props.busy ? (
              <Loader2Icon className="size-3.5 animate-spin" />
            ) : (
              <PlayIcon className="size-3.5" />
            )}
            {props.cardVm.actionLabel}
          </button>
          <button
            type="button"
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={props.onShowTerminals}
          >
            <TerminalSquareIcon className="size-3.5" />
            Show terminals
          </button>
        </div>
      </div>
    </div>
  );
}

function terminalSessionObservation(snapshot: TerminalSessionSnapshot) {
  return { text: snapshot.history, observedAt: snapshot.updatedAt };
}

function terminalEventEntryObservation(entry: TerminalEventEntry) {
  const event = entry.event;
  if (event.type === "output") {
    return { text: event.data, observedAt: event.createdAt };
  }
  if (event.type === "started" || event.type === "restarted") {
    return { text: event.snapshot.history, observedAt: event.createdAt };
  }
  return { text: "", observedAt: event.createdAt };
}

function terminalIdObservations(
  input: LatestThreadDevServerPreviewUrlInput,
  terminalId: string,
): Array<{ text: string; observedAt?: string | undefined }> {
  const snapshot = selectTerminalSessionSnapshot(
    input.terminalSessionSnapshotByKey,
    input.threadRef,
    terminalId,
  );
  const entries = selectTerminalEventEntries(
    input.terminalEventEntriesByKey,
    input.threadRef,
    terminalId,
  );

  return [
    ...(snapshot ? [terminalSessionObservation(snapshot)] : []),
    ...entries.map(terminalEventEntryObservation),
  ];
}

function terminalPreviewUrlObservation(record: TerminalPreviewUrlRecord | null | undefined) {
  return record?.url ? [{ text: record.url, observedAt: record.observedAt }] : [];
}

function collectThreadTerminalObservations(input: LatestThreadDevServerPreviewUrlInput) {
  const threadSnapshots = selectThreadTerminalSessionSnapshots(
    input.terminalSessionSnapshotByKey,
    input.threadRef,
  );
  return [
    ...threadSnapshots.map(terminalSessionObservation),
    ...input.terminalState.terminalIds.flatMap((terminalId) =>
      terminalIdObservations(input, terminalId),
    ),
    ...terminalPreviewUrlObservation(input.terminalPreviewUrlRecord),
  ];
}

function collectThreadMessageObservations(
  messages: ReadonlyArray<BrowserPreviewMessageInput>,
): Array<{ text: string; observedAt?: string | undefined }> {
  return messages.map((message) => ({
    text: message.text,
    observedAt: message.completedAt ?? message.createdAt,
  }));
}

export function latestThreadDevServerPreviewUrlFromSources(
  input: LatestThreadDevServerPreviewUrlInput,
): string | null {
  const latestTerminalUrl = latestObservedDevServerPreviewUrl(
    collectThreadTerminalObservations(input),
  );
  if (latestTerminalUrl) {
    return latestTerminalUrl;
  }

  return latestObservedDevServerPreviewUrl(collectThreadMessageObservations(input.threadMessages));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForTerminalDevServerPreviewUrl(input: {
  readonly threadRef: ReturnType<typeof scopeThreadRef>;
  readonly terminalId: string;
  readonly timeoutMs?: number;
  readonly pollMs?: number;
}): Promise<string | null> {
  const timeoutMs = input.timeoutMs ?? DEV_SERVER_READY_TIMEOUT_MS;
  const pollMs = input.pollMs ?? DEV_SERVER_READY_POLL_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    const entries = selectTerminalEventEntries(
      useTerminalStateStore.getState().terminalEventEntriesByKey,
      input.threadRef,
      input.terminalId,
    );
    const url = latestTerminalDevServerPreviewUrl(entries);
    if (url) {
      return url;
    }
    await sleep(pollMs);
  }

  return null;
}

interface BrowserDevServerLauncherOptions {
  readonly blockingBrowserError: string | null;
  readonly browserThreadContext: {
    readonly id: ThreadId;
    readonly worktreePath: string | null;
  } | null;
  readonly devServerLaunchBusy: boolean;
  readonly devServerLaunchPlan: ReturnType<typeof resolveDevServerLaunchPlan>;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentApi: EnvironmentApi | undefined;
  readonly ensureDevTunnelPreviewUrl: (input: {
    readonly allocatedPort: number;
    readonly label?: string;
  }) => Promise<string>;
  readonly ensureSession: (
    initialUrl: string,
    options?: {
      readonly allowWhileConnecting?: boolean;
      readonly retryHiddenLocalPreviewFailure?: boolean;
    },
  ) => Promise<boolean>;
  readonly liveDisplayIsAttached: boolean;
  readonly previewRememberLastUrl: boolean;
  readonly project: { readonly cwd: string; readonly name: string } | null | undefined;
  readonly runningTerminalCount: number;
  readonly latestThreadDevServerPreviewUrl: string | null;
  readonly session: BrowserSessionSnapshot | null;
  readonly setAddressValue: (value: string) => void;
  readonly setConnecting: (value: boolean) => void;
  readonly setLaunchedDevScriptId: (value: string | null) => void;
  readonly setThreadDevPort: (value: number | null) => void;
  readonly setThreadDevPreviewUrl: (value: string | null) => void;
  readonly storeNewTerminal: (threadRef: ScopedThreadRef, terminalId: string) => void;
  readonly storeSetActiveTerminal: (threadRef: ScopedThreadRef, terminalId: string) => void;
  readonly storeSetTerminalLaunchContext: (
    threadRef: ScopedThreadRef,
    context: TerminalLaunchContext,
  ) => void;
  readonly storeSetTerminalOpen: (threadRef: ScopedThreadRef, open: boolean) => void;
  readonly threadDevPort: number | null;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
  readonly updateSettings: (patch: { readonly previewLastUrl: string }) => void;
  readonly restartBrowserSessionAtUrl: (url: string) => Promise<boolean>;
  readonly runAddressCommand: (command: BrowserAddressCommand) => Promise<boolean>;
}

export function useBrowserDevServerLauncher(
  input: BrowserDevServerLauncherOptions,
): () => Promise<void> {
  const openDevServerPreviewUrl = useCallback(
    async (previewInput: {
      readonly previewUrl: string;
      readonly displayPreviewUrl: string;
      readonly retryHiddenLocalPreviewFailure?: boolean;
    }): Promise<boolean> => {
      input.setLaunchedDevScriptId(input.devServerLaunchPlan.id);
      input.setAddressValue(previewInput.displayPreviewUrl);
      const previewAction = resolveBrowserDevServerPreviewAction({
        hasSession: Boolean(input.session),
        liveDisplayIsAttached: input.liveDisplayIsAttached,
        hasBlockingError: input.blockingBrowserError !== null,
      });
      let ok = false;
      if (previewAction === "restart-session") {
        ok = await input.restartBrowserSessionAtUrl(previewInput.previewUrl);
      } else if (previewAction === "new-tab") {
        ok = await input.runAddressCommand({
          type: "newTab",
          url: previewInput.previewUrl,
          makeActive: true,
        });
      } else {
        ok = await input.ensureSession(previewInput.previewUrl, {
          allowWhileConnecting: true,
          ...(previewInput.retryHiddenLocalPreviewFailure === undefined
            ? {}
            : { retryHiddenLocalPreviewFailure: previewInput.retryHiddenLocalPreviewFailure }),
        });
      }
      if (ok) {
        input.setAddressValue(previewInput.displayPreviewUrl);
        if (input.previewRememberLastUrl) {
          input.updateSettings({ previewLastUrl: previewInput.displayPreviewUrl });
        }
      }
      return ok;
    },
    [input],
  );

  const tryOpenRunningDevServerPreview = useCallback(async (): Promise<{
    readonly opened: boolean;
    readonly preferredLaunchPort: number | null;
  }> => {
    if (!input.project || !input.environmentApi) {
      return { opened: false, preferredLaunchPort: null };
    }
    const runningPreviewLaunch = resolveRunningDevServerPreviewLaunch({
      runningTerminalCount: input.runningTerminalCount,
      latestThreadDevServerPreviewUrl: input.latestThreadDevServerPreviewUrl,
    });
    if (!runningPreviewLaunch) {
      return { opened: false, preferredLaunchPort: null };
    }

    const previewUrl = await input.ensureDevTunnelPreviewUrl({
      allocatedPort: runningPreviewLaunch.allocatedPort,
      label: `${input.project.name} dev server`,
    });
    const opened = await openDevServerPreviewUrl({
      previewUrl,
      displayPreviewUrl: runningPreviewLaunch.displayPreviewUrl,
      retryHiddenLocalPreviewFailure: false,
    });
    if (opened) {
      return { opened: true, preferredLaunchPort: null };
    }

    await input.environmentApi.threadDevRuntime
      ?.release({ threadId: input.threadId })
      .catch(() => undefined);
    input.setThreadDevPort(null);
    input.setThreadDevPreviewUrl(null);
    return { opened: false, preferredLaunchPort: runningPreviewLaunch.allocatedPort };
  }, [input, openDevServerPreviewUrl]);

  const releaseDevRuntimeBeforeLaunch = useCallback(async () => {
    if (
      !input.environmentApi ||
      !shouldReleaseDevRuntimeBeforeLaunch({
        runningTerminalCount: input.runningTerminalCount,
      })
    ) {
      return;
    }
    await input.environmentApi.threadDevRuntime
      ?.release({ threadId: input.threadId })
      .catch(() => undefined);
    input.setThreadDevPort(null);
    input.setThreadDevPreviewUrl(null);
  }, [input]);

  const updateDevServerPreviewFromDetectedUrl = useCallback(
    async (previewInput: {
      readonly detectedPreviewUrl: string | null;
      readonly allocatedPort: number;
      readonly fallbackPreviewUrl: string;
      readonly fallbackDisplayPreviewUrl: string;
      readonly projectName: string;
    }): Promise<{ readonly previewUrl: string; readonly displayPreviewUrl: string }> => {
      const detectedPort = previewInput.detectedPreviewUrl
        ? localDevServerPreviewPort(previewInput.detectedPreviewUrl)
        : null;
      if (!detectedPort || detectedPort === previewInput.allocatedPort) {
        return {
          previewUrl: previewInput.fallbackPreviewUrl,
          displayPreviewUrl: previewInput.fallbackDisplayPreviewUrl,
        };
      }

      const previewUrl = await input.ensureDevTunnelPreviewUrl({
        allocatedPort: detectedPort,
        label: `${previewInput.projectName} dev server`,
      });
      const displayPreviewUrl = `http://localhost:${detectedPort}/`;
      input.setThreadDevPort(detectedPort);
      input.setAddressValue(displayPreviewUrl);
      return { previewUrl, displayPreviewUrl };
    },
    [input],
  );

  const launchDevServerTerminal = useCallback(
    async (previewInput: {
      readonly preferredLaunchPort: number | null;
      readonly launchProject: { readonly cwd: string; readonly name: string };
      readonly launchThreadContext: { readonly worktreePath?: string | null };
    }): Promise<{ readonly previewUrl: string; readonly displayPreviewUrl: string }> => {
      const targetTerminalId = BROWSER_PREVIEW_TERMINAL_ID;
      const worktreePath = previewInput.launchThreadContext.worktreePath ?? null;
      const targetCwd = worktreePath ?? previewInput.launchProject.cwd;

      await releaseDevRuntimeBeforeLaunch();

      const devRuntime = await input.environmentApi!.threadDevRuntime?.allocatePort({
        threadId: input.threadId,
      });
      const allocatedPort =
        devRuntime?.allocatedPort ??
        input.threadDevPort ??
        previewInput.preferredLaunchPort ??
        5733;
      input.setThreadDevPort(allocatedPort);
      const previewUrl = await input.ensureDevTunnelPreviewUrl({
        allocatedPort,
        label: `${previewInput.launchProject.name} dev server`,
      });
      const displayPreviewUrl = resolveDevServerDisplayPreviewUrl({
        previewUrl,
        allocatedPort,
        devTunnelRecords: input.devTunnelRecordsRef.current,
      });
      input.setAddressValue(displayPreviewUrl);

      const runtimeEnv = {
        ...projectScriptRuntimeEnv({
          project: {
            cwd: previewInput.launchProject.cwd,
          },
          worktreePath,
        }),
        PORT: String(allocatedPort),
        VITE_PORT: String(allocatedPort),
        FORGE_DEV_PORT: String(allocatedPort),
        BROWSER: "none",
      };
      const launchCommand = buildPortAwareDevServerCommand({
        command: input.devServerLaunchPlan.command,
        allocatedPort,
        source: input.devServerLaunchPlan.source,
      });

      input.storeSetTerminalLaunchContext(input.threadRef, {
        terminalId: targetTerminalId,
        cwd: targetCwd,
        worktreePath,
        runtimeEnv,
      });
      input.storeSetTerminalOpen(input.threadRef, true);
      input.storeNewTerminal(input.threadRef, targetTerminalId);
      input.storeSetActiveTerminal(input.threadRef, targetTerminalId);

      await input.environmentApi!.terminal.open({
        threadId: input.threadId,
        terminalId: targetTerminalId,
        cwd: targetCwd,
        ...(worktreePath !== null ? { worktreePath } : {}),
        env: runtimeEnv,
        cols: SCRIPT_TERMINAL_COLS,
        rows: SCRIPT_TERMINAL_ROWS,
      });
      await input.environmentApi!.terminal.write({
        threadId: input.threadId,
        terminalId: targetTerminalId,
        data: `${launchCommand}\r`,
      });

      const detectedPreviewUrl = await waitForTerminalDevServerPreviewUrl({
        threadRef: input.threadRef,
        terminalId: targetTerminalId,
      });
      return updateDevServerPreviewFromDetectedUrl({
        detectedPreviewUrl,
        allocatedPort,
        fallbackPreviewUrl: previewUrl,
        fallbackDisplayPreviewUrl: displayPreviewUrl,
        projectName: previewInput.launchProject.name,
      });
    },
    [input, releaseDevRuntimeBeforeLaunch, updateDevServerPreviewFromDetectedUrl],
  );

  return useCallback(async () => {
    if (
      !shouldRunDevServerLaunch({
        launchBusy: input.devServerLaunchBusy,
        hasProject: Boolean(input.project),
        hasBrowserThreadContext: Boolean(input.browserThreadContext),
        hasEnvironmentApi: Boolean(input.environmentApi),
      })
    ) {
      return;
    }
    const launchProject = input.project!;
    const launchThreadContext = input.browserThreadContext!;

    try {
      input.setConnecting(true);
      const runningPreviewResult = await tryOpenRunningDevServerPreview();
      if (runningPreviewResult.opened) {
        return;
      }

      const { previewUrl, displayPreviewUrl } = await launchDevServerTerminal({
        preferredLaunchPort: runningPreviewResult.preferredLaunchPort,
        launchProject,
        launchThreadContext,
      });

      await openDevServerPreviewUrl({ previewUrl, displayPreviewUrl });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not start dev server",
        description:
          error instanceof Error
            ? error.message
            : `Failed to run "${input.devServerLaunchPlan.name}".`,
      });
    } finally {
      input.setConnecting(false);
    }
  }, [input, launchDevServerTerminal, openDevServerPreviewUrl, tryOpenRunningDevServerPreview]);
}
